<template>
  <div>
    <h3 class="mb-1 mb-sm-2 mt-4">
      {{ $t("signup.contact_review_component.generator_owner_title") }}
    </h3>
    <p class="mb-2 mb-sm-4">
      {{ $t("signup.contact_review_component.generator_owner_subtext") }}
    </p>

    <!-- Company Search (initial view if fields are pristine) -->
    <template v-if="showSearchUI">
      <!-- Slot for Country Specific QuickFill Component -->
      <slot name="quickfill" :handleSelect="handleQuickFill"></slot>
      <div class="text-center mt-4">
        <button
          type="button"
          class="btn btn-link text-decoration-none"
          @click="showCompanyForm"
        >
          <font-awesome-icon icon="pencil-alt" class="me-1" />
          {{ $t("signup.fi.quickfill.manual_entry") }}
        </button>
      </div>
    </template>

    <!-- Company Form (after selection or manual entry) -->
    <template v-else>
      <div class="mb-6 d-flex flex-column gap-2 flex-grow-1">
        <!-- Search state (shown when quickfill button is clicked) -->
        <div v-if="showQuickFill" class="p-3 pb-4 bg-light rounded">
          <div class="d-flex justify-content-between align-items-baseline mb-2">
            <p class="text-muted fw-semibold mb-0">
              <font-awesome-icon icon="search" class="me-1" />
              {{ $t("signup.fi.quickfill.search") }}
            </p>
            <button
              class="btn btn btn-link ms-auto p-2"
              @click="showQuickFill = false"
            >
              <font-awesome-icon icon="times" class="fs-8" />
            </button>
          </div>
          <!-- Slot for Country Specific QuickFill Component -->
          <slot name="quickfill" :handleSelect="handleQuickFill"></slot>
        </div>

        <div v-else>
          <button
            type="button"
            class="btn btn-link p-0"
            @click="showCompanySearch"
          >
            <font-awesome-icon icon="search" class="me-1" />
            {{ $t("signup.fi.quickfill.search") }}
          </button>
        </div>
      </div>
    </template>

    <!-- Hidden, not unmounted, behind the search: the fields' rules still gate the step. -->
    <div v-show="!showSearchUI">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import useVuelidate from "@vuelidate/core";
import { computed, onMounted, ref, watch } from "vue";

/**
 * The company's fields go in the default slot; they show once the search is set aside. The search
 * shows first until the company has details or its fields were all checked, e.g. by Next.
 */
const props = defineProps<{
  hasCompanyDetails: boolean;
}>();

const emit = defineEmits<{
  "quickfill-select": [selectedCompanyData: any];
}>();

// Collects the slotted company fields alone, so the signatory's fields can't mark the company edited.
const v$ = useVuelidate();
const isDataPristine = computed(
  () => !props.hasCompanyDetails && !v$.value.$dirty
);

const showQuickFill = ref(false);
const showSearchUI = ref(isDataPristine.value);

// Show manual form instead of search
const showCompanyForm = () => {
  showSearchUI.value = false;
  showQuickFill.value = false;
};

// Show search quickfill from form view
const showCompanySearch = () => {
  showQuickFill.value = true;
};

const handleQuickFill = (selectedCompanyData: any) => {
  emit("quickfill-select", selectedCompanyData);
  // A pick can leave fields the registry doesn't hold: show what is still missing right away.
  v$.value.$touch();
  showCompanyForm(); // Switch to form view after selection
};

watch(isDataPristine, (newVal: boolean) => {
  showSearchUI.value = newVal;
});

onMounted(() => {
  showSearchUI.value = isDataPristine.value;
});
</script>

<style scoped lang="scss">
.bg-light {
  /* Consider defining a variable or using Bootstrap's default */
  background-color: #f8f9fa !important;
}
</style>
